import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  CardBody,
  CardTitle,
  Row,
  Col,
  Table,
} from "reactstrap";
import { Line } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from "chart.js";
import {
  getAPIURL,
  getVendorID,
  getFormattedPrice,
} from "../../services/helper";
import http from "../../services/http";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const SalesHistory = () => {
  const api_url = getAPIURL();
  const vendorId = getVendorID();
  const [data, setData] = useState({
    summary: {},
    month_headers: [],
    years_data: [],
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchSalesHistory();
  }, []);

  const fetchSalesHistory = async () => {
    setLoading(true);
    try {
      const { data } = await http.get(
        `${api_url}/reports/sales/history?vendor_id=${vendorId}`
      );
      setData(data);
    } catch (error) {
      console.error("Error fetching sales history:", error);
    }
    setLoading(false);
  };

  return (
    <div className="page-body">
      <Container className="mt-2">
        <h2 className="text-center mb-4">Sales History</h2>

        {/* Summary Cards */}
        {data.summary && (
          <Row className="mb-4">
            <Col md={4}>
              <Card className="text-center">
                <CardBody>
                  <h5>Total Sales</h5>
                  <h3>{data.summary.total_sales}</h3>
                </CardBody>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="text-center">
                <CardBody>
                  <h5>Total Amount</h5>
                  <h3>{getFormattedPrice(data.summary.total_amount)}</h3>
                </CardBody>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="text-center">
                <CardBody>
                  <h5>Date Range</h5>
                  <p>
                    {data.summary.date_range?.start_date} to{" "}
                    {data.summary.date_range?.end_date}
                  </p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        {/* Cross Table */}
        <Card>
          <CardBody>
            <CardTitle tag="h5">Sales by Year and Month</CardTitle>
            {loading ? (
              <p>Loading...</p>
            ) : (
              <Table striped responsive>
                <thead>
                  <tr>
                    <th>Year</th>
                    {data.month_headers.map((month) => (
                      <th key={month.month_number}>{month.month_name}</th>
                    ))}
                    <th>Year Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.years_data.map((yearData) => (
                    <tr key={yearData.year}>
                      <td>
                        <strong>{yearData.year}</strong>
                      </td>
                      {data.month_headers.map((month) => {
                        const monthData = yearData.months[
                          month.month_number
                        ] || { sales_count: 0, total_amount: 0 };
                        return (
                          <td key={month.month_number}>
                            {getFormattedPrice(monthData.total_amount)}({monthData.sales_count})
                          </td>
                        );
                      })}
                      <td>
                        <strong>{getFormattedPrice(yearData.year_total)}({yearData.year_sales_count})</strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </CardBody>
        </Card>

        {/* Line Chart */}
        {data.years_data.length > 0 && (
          <Card className="mt-4">
            <CardBody>
              <CardTitle tag="h5">Sales Trend</CardTitle>
              <Line
                data={{
                  labels: data.month_headers.map(m => m.month_name),
                  datasets: data.years_data.map((yearData, index) => {
                    const colors = ['#36A2EB', '#FF6384', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4'];
                    return {
                      label: yearData.year,
                      data: data.month_headers.map(month => {
                        const monthData = yearData.months[month.month_number] || { total_amount: 0 };
                        return monthData.total_amount;
                      }),
                      borderColor: colors[index % colors.length],
                      backgroundColor: colors[index % colors.length],
                      tension: 0.1
                    };
                  })
                }}
                options={{
                  responsive: true,
                  plugins: {
                    legend: {
                      position: 'top',
                    },
                    title: {
                      display: true,
                      text: 'Monthly Sales Amount by Year'
                    }
                  },
                  scales: {
                    y: {
                      beginAtZero: true
                    }
                  }
                }}
              />
            </CardBody>
          </Card>
        )}
      </Container>
    </div>
  );
};

export default SalesHistory;
